import { useCallback, useContext } from "react"

import { Link, useLocalSearchParams } from "expo-router"

import {
  AccountCollection,
  AccountData,
  ClientCollection,
  Instrument,
  InvestmentTransaction,
  InvestmentTransactionCollection,
} from "wealthmode-frontend/backend"
import { Box, HStack, Icon } from "wealthmode-frontend/gluestack/ui"
import { XIcon } from "wealthmode-frontend/icons"
import { formatMoneyFull } from "wealthmode-frontend/util"

import { DataTable, IDLink } from "@/components"
import { BackendContext } from "@/context"

export default function InvestmentTransactions() {
  const { client_id, account_id } = useLocalSearchParams<{
    client_id: string
    account_id: string
  }>()

  const backend = useContext(BackendContext)

  const load = useCallback(async () => {
    const clients = backend.collection("clients") as ClientCollection
    const client = clients.model(client_id)
    await client.fetch({ reload: true })

    const accounts = client.link("accounts")?.resolve() as AccountCollection
    const account = accounts.model(account_id)
    await account.fetch({ reload: true })

    const data = account.embed("data") as AccountData
    const transactions = data.link("investment_transactions")?.resolve() as InvestmentTransactionCollection | undefined
    if (!transactions) {
      return undefined
    }

    await transactions.fetch({ reload: true })

    return transactions
  }, [backend, client_id, account_id])

  return (
    <Box>
      <HStack>
        <Box className="flex-1">
          <DataTable
            title={"Investment Transactions"}
            load={load}
            header={[
              { id: "id", title: "ID", shrink: true },
              { id: "date", title: "Date" },
              { id: "category", title: "Category" },
              { id: "kind", title: "Kind" },
              { id: "instrument", title: "Instrument" },
              { id: "description", title: "Description" },
              { id: "price", title: "Price" },
              { id: "quantity", title: "Quantity" },
              { id: "fees", title: "Fees" },
              { id: "holding_delta", title: "Holding Δ" },
              { id: "instrument_delta", title: "Instrument Δ" },
              { id: "cash_delta", title: "Cash Δ" },
              { id: "value_delta", title: "Value Δ" },
              { id: "identities_valid", title: "Identities Valid" },
            ]}
            render={(model: InvestmentTransaction, field: string) => {
              switch (field) {
                case "id":
                  return (
                    <IDLink
                      href={`/clients/${client_id}/accounts/${account_id}/investment_transactions/${model.attributes.id}`}
                      id={model.attributes.id}
                    />
                  )

                case "instrument":
                  const sec = model.embed("instrument") as Instrument | null
                  if (sec == null) {
                    return null
                  }
                  return (
                    <Link href={`/instruments/${sec.attributes.id}`}>
                      {sec.attributes.symbol ?? (sec.attributes.type == "cash" ? "Cash" : sec.attributes.type)}
                    </Link>
                  )

                case "price":
                case "fees":
                case "instrument_delta":
                case "cash_delta":
                case "value_delta":
                  return formatMoneyFull(model.get(field))

                case "identities_valid":
                  return model.get(field) ? null : <Icon as={XIcon} />
              }
              return model.get(field)
            }}
          />
        </Box>
      </HStack>
    </Box>
  )
}
